import CloudSyncIcon from "@mui/icons-material/CloudSync";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fab,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import BaseModelNameDisplayer from "components/BaseModelNameDisplayer";
import { ClaimBasedComponent } from "components/ClaimBasedComponent";
import ConfirmDialog from "components/ConfirmDialog";
import DualListWithLinks, {
  DualListLeftItem,
} from "components/DualListWithLinks";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import StatusChip from "components/StatusChip";
import { hasUserPermission } from "helpers/ClaimHelpers";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import ImpersonateUserButton from "pages/UserListPage/ImpersonateUserButton";
import { UserAuditLogAccordion } from "pages/UserListPage/UserAuditLogAccordion";
import { UserSkusList } from "pages/UserListPage/UserSkusList";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { OrganizationUserDetails } from "services/KnowfullyAdminClient";
import { ClaimPermission, ClaimResource, ParentClaim } from "types/claimTypes";
import { Severity, SnackbarMessageProps } from "types/common-types";
import ApplicationUserList from "./ApplicationUsers/ApplicationUserList";
import RoleField from "./RolesField";
import UserCredentialList from "./UserCredentials/UserCredentialList";

const getStyles = (theme: Theme): { [key: string]: SxProps } => ({
  fabWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 56,
    height: 56,
    borderRadius: "50%",
    boxShadow: theme.shadows[6],
    transition: theme.transitions.create(["background-color", "box-shadow"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[8],
    },
  },
});

const UserDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const {
    organizationService,
    organizationUserService,
    userService,
    usersUrl,
    userEditUrl,
    userCertificatesToolUrl,
    userSubmissionsToolUrl,
    bulkEnrollmentUrl,
    usersListUrl,
    viewUserDesignationsChangeLogUrl,
    userProfileManagementUrl,
  } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerPromise } = useTrashablePromises();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const styles = getStyles(useTheme());
  const syncUsersAllowed = useMemo(
    () =>
      hasUserPermission(
        ParentClaim.Users,
        ClaimResource.Users,
        ClaimPermission.Sync
      ),
    []
  );

  const userResult = useStrictQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", id],
    () => organizationUserService.getOrganizationUserDetails(id!),
    {
      enabled: !!id,
    }
  );

  const data = userResult.data;
  const name = getUserDisplayName(data, "User Details");
  useTitle(name);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get("error");
    if (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(decodeURIComponent(error), Severity.Error)
      );
      // Remove error param from URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  if (!id) return <Navigate to={usersUrl()} replace />;
  if (userResult.isLoading) return <Loader />;

  const user = data!;

  const {
    firstName,
    lastName,
    email,
    externalSourceId,
    roles,
    timeZone,
  } = user;

  const rows = [
    {
      title: t({ id: "id" }),
      component: user.id && <span>{user.id}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: user.organizationId && (
        <BaseModelNameDisplayer
          id={user.organizationId}
          cacheKey="organization"
          fetcher={() =>
            organizationService.getOrganization(user.organizationId)
          }
        />
      ),
    },
    {
      title: t({ id: "firstNameLabel" }),
      component: firstName && <span>{firstName}</span>,
    },
    {
      title: t({ id: "lastNameLabel" }),
      component: lastName && <span>{lastName}</span>,
    },
    {
      title: t({ id: "emailLabel" }),
      component: email && <span>{email}</span>,
    },
    {
      title: t({ id: "timeZoneLabel" }),
      component: timeZone && <span>{timeZone}</span>,
    },
    {
      title: t({ id: "statusLabel" }),
      component: StatusChip({ value: user }),
    },
    {
      title: t({ id: "externalSourceId" }),
      component: externalSourceId && <span>{externalSourceId}</span>,
    },
    {
      title: t({ id: "rolesLabel" }),
      component: (
        <RoleField
          disabled={true}
          value={roles}
          onChange={() => false}
        />
      ),
    },
  ].filter((r) => !!r.component);

  const tools: DualListLeftItem[] = [
    {
      text: t({ id: "userManagement" }),
      rightListItems: [
        {
          text: t({ id: "profileManagement" }),
          href: userProfileManagementUrl(id),
        },
      ],
    },
    {
      text: t({ id: "enrollmentManagement" }),
      rightListItems: [
        {
          text: t({ id: "addEnrollment" }),
          href: bulkEnrollmentUrl(id),
        },
      ],
    },
    {
      text: t({ id: "certificates" }),
      rightListItems: [
        {
          text: t({ id: "viewModifyCerts" }),
          href: userCertificatesToolUrl(id),
        },
      ],
    },
    {
      text: t({ id: "creditSubmission" }),
      rightListItems: [
        {
          text: t({ id: "viewModifySubmissions" }),
          href: userSubmissionsToolUrl(id),
        },
      ],
    },
    {
      text: t({ id: "userDesignationsChangeLog" }),
      rightListItems: [
        {
          text: t({ id: "viewUserDesignationsChangeLog" }),
          href: viewUserDesignationsChangeLogUrl(id),
        },
      ],
    },
  ];

  const handleSyncConfirm = async () => {
    try {
      await registerPromise(userService.syncUsers([user.id]));
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "userSyncSuccess" }))
      );
    } catch {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "userSyncError" }), Severity.Error)
      );
    }
    setShowSyncDialog(false);
  };

  const handleSync = () => {
    setShowSyncDialog(true);
  };

  return (
    <>
      <RedirectBackButton
        redirectRoute={usersListUrl()}
        labelKey="backToListButtonLabel"
      />

      <h1>
        {user.firstName} {user.lastName}
      </h1>

      <Box mb={3} justifyContent="center" display="flex">
        <Fab color="primary" onClick={() => navigate(userEditUrl(id))}>
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Box sx={styles.fabWrapper}>
          <ImpersonateUserButton user={user} fullWidth />
        </Box>
        <Box ml={4} />
        {syncUsersAllowed && (
          <Tooltip title={t({ id: "syncUser" })}>
            <Fab color="primary" onClick={handleSync}>
              <CloudSyncIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>

      <Box mb={4}>
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    {title}
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="tools" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <DualListWithLinks listItems={tools} />
          </Box>
        </AccordionDetails>
      </Accordion>

      <UserSkusList userId={user.id} organizationId={user.organizationId} />

      <ApplicationUserList userId={user.id} />
      <UserCredentialList userId={user.id} readonly />

      <ClaimBasedComponent requestedClaims={[ClaimResource.UserAuditLog]}>
        <UserAuditLogAccordion userId={user.id} />
      </ClaimBasedComponent>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
      <ConfirmDialog
        dialogTitleKey="syncUser"
        contentKey="syncUserConfirmation"
        open={showSyncDialog}
        onCancel={() => setShowSyncDialog(false)}
        handleConfirm={handleSyncConfirm}
      />
    </>
  );
};

export default UserDetails;
